// 工事マスタ画面。
// - 一覧 (WorkListView): 工事一覧 + 概要サマリ(入荷予定/入荷済/施工済の材料数)。別ファイルに切り出している。
// - 詳細 (WorkDetailView): 3 タブ (工事情報 / 場所区分ラベル(参照) / アクセス範囲(参照))
// 一覧で「編集を開く」or「新規工事作成」を押すと詳細ビューへ切り替わる。

import {
  Box,
  Stack,
  Typography,
  Chip,
  Paper,
  Alert,
  Button,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Divider,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useState, useMemo } from "react";
import { ExtendedDB } from "../../../types/extendedDB";
import { useFullDb, replaceCollection, nextIdFor } from "../../../api/dbSlice";
import { useIsOnline } from "../../../util/useIsOnline";
import { OfflineMasterBanner } from "../../../components/OfflineMasterBanner";
import { PageContext } from "../PageContext";
import { WORD } from "../../../word";
import { renderWord } from "../../../util/renderWord";
import Time from "../../../util/Time";
import type { WorkBase } from "../../../schema/generated/Work";
import type { WorkTechnicianBase } from "../../../schema/generated/WorkTechnician";
import { errorMessageOf, required } from "../../../util/typed";
import { ContentLoading } from "../../../components/Loading";
import { WorkListView } from "./WorkListView";

type DetailTab = "info" | "labels" | "access";

// 高架下条件の選択肢(mock 文言に統一)。
const OVERHEAD_CONDITION_OPTIONS = ["道路（主に車道）", "道路（主に歩道）", "鉄道", "公園", "その他"];

// 高架下条件の選択肢。保存済みの旧文言が選択肢に無い場合は、select が空にならないよう動的に含める。
function overheadConditionOptions(current: string): string[] {
  if (current !== "" && !OVERHEAD_CONDITION_OPTIONS.includes(current)) {
    return [...OVERHEAD_CONDITION_OPTIONS, current];
  }
  return OVERHEAD_CONDITION_OPTIONS;
}

// ===== Root =====
export default function WorkMasterPage({ pageContext, showOfflineBanner = true }: { pageContext: PageContext; showOfflineBanner?: boolean }) {
  const { db, loading } = useFullDb(pageContext.api.indexedDB, pageContext.api.currentUser.id);
  const isOnline = useIsOnline(pageContext.api);
  // workId: null = 一覧, number = 既存編集, "new" = 新規作成
  const [editTarget, setEditTarget] = useState<number | "new" | null>(null);

  if (loading) {
    return <ContentLoading />;
  }

  if (editTarget === null) {
    return (
      <WorkListView
        db={db}
        isOnline={isOnline}
        showOfflineBanner={showOfflineBanner}
        onSelect={(id) => setEditTarget(id)}
        onCreateNew={() => setEditTarget("new")}
      />
    );
  }

  if (editTarget === "new") {
    return (
      <WorkDetailView
        work={null}
        db={db}
        pageContext={pageContext}
        isOnline={isOnline}
        showOfflineBanner={showOfflineBanner}
        onBack={() => setEditTarget(null)}
      />
    );
  }

  const work = db.works.find((w) => w.id === editTarget) ?? null;
  if (work === null) {
    return (
      <WorkListView
        db={db}
        isOnline={isOnline}
        showOfflineBanner={showOfflineBanner}
        onSelect={(id) => setEditTarget(id)}
        onCreateNew={() => setEditTarget("new")}
      />
    );
  }
  return (
    <WorkDetailView
      work={work}
      db={db}
      pageContext={pageContext}
      isOnline={isOnline}
      showOfflineBanner={showOfflineBanner}
      onBack={() => setEditTarget(null)}
    />
  );
}

// ===== 詳細 =====
function WorkDetailView({
  work,
  db,
  pageContext,
  isOnline,
  showOfflineBanner,
  onBack,
}: {
  work: WorkBase | null;
  db: ExtendedDB;
  pageContext: PageContext;
  isOnline: boolean;
  showOfflineBanner: boolean;
  onBack: () => void;
}) {
  const [tab, setTab] = useState<DetailTab>("info");
  const isNew = work === null;

  const displayName = work === null ? "新規工事" : (work.short_name || work.official_name || `#${work.id}`);

  // サマリ統計
  const summary = useMemo(() => {
    if (work === null) {
      return { inputs: 0, outputs: 0, areas: 0 };
    }
    const inputs = db.carryIns.filter((c) => c.work_id === work.id && c.deleted_at == null).length;
    const areas = db.constructionPlanAreas.filter((a) => a.work_id === work.id && a.deleted_at == null).length;
    const outputs = areas;
    return { inputs, outputs, areas };
  }, [work, db.carryIns, db.constructionPlanAreas]);

  return (
    <Stack gap={2}>
      <Breadcrumbs>
        <Link component="button" onClick={onBack} underline="hover" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBackIcon fontSize="small" /> 工事マスタ
        </Link>
        <Typography color="text.primary">{displayName}</Typography>
      </Breadcrumbs>

      {showOfflineBanner && <OfflineMasterBanner isOnline={isOnline} />}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <EngineeringIcon color="action" />
          <Typography variant="h6" fontWeight="bold">{displayName}</Typography>
          {work !== null && work.control_number !== "" && (
            <Chip size="small" label={work.control_number} />
          )}
          {isNew && <Chip size="small" color="warning" label="新規" />}
          <Box flex={1} />
          <Button variant="text" size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>工事一覧へ戻る</Button>
        </Stack>
        {work !== null && (
          <Typography variant="body2" color="text.secondary" mt={0.5}>{work.official_name}</Typography>
        )}
        {work !== null && (
          <Stack direction="row" gap={3} mt={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">工事ID</Typography>
              <Typography variant="body2">#{work.id}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">搬入予定数</Typography>
              <Typography variant="body2">{summary.inputs}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">施工箇所数</Typography>
              <Typography variant="body2">{summary.areas}</Typography>
            </Box>
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="info" label="工事情報" />
          <Tab value="labels" label="場所区分ラベル設定（参照のみ）" disabled={isNew} />
          <Tab value="access" label="ユーザーアクセス範囲" disabled={isNew} />
        </Tabs>
        <Divider />
        <Box p={3}>
          {tab === "info" && (
            <InfoEditTab work={work} db={db} pageContext={pageContext} onSaved={(savedId) => {
              if (isNew) {
                onBack();
                // 一覧へ戻った後に直接開きたいが、状態が親側にしかないため戻るだけにとどめる
              }
              void savedId;
            }} />
          )}
          {tab === "labels" && work !== null && <LabelSettingTab work={work} db={db} />}
          {tab === "access" && work !== null && <AccessScopeTab work={work} db={db} />}
        </Box>
      </Paper>
    </Stack>
  );
}

// ===== info タブ =====
type WorkDraft = {
  id: number | null;
  area_division_id: number | null;
  control_number: string;
  short_name: string;
  official_name: string;
  client_id: number | null;
  client_department: string;
  contractor_name: string;
  location: string;
  period_start: string;
  period_end: string;
  paint_kind: string;
  structure_form: string;
  environment_category: string;
  overhead_condition: string;
};

type TechnicianDraft = {
  id: number | null;             // null は未保存(新規行)
  origId: number | null;         // 元の id を保持(diff 比較用)
  name: string;
  affiliation: string;
  role: string;
  sort_order: number;
  deleted: boolean;
};

function makeWorkDraft(work: WorkBase | null): WorkDraft {
  if (work === null) {
    return {
      id: null,
      area_division_id: null,
      control_number: "",
      short_name: "",
      official_name: "",
      client_id: null,
      client_department: "",
      contractor_name: "",
      location: "",
      period_start: "",
      period_end: "",
      paint_kind: "",
      structure_form: "",
      environment_category: "",
      overhead_condition: "",
    };
  }
  return {
    id: work.id,
    area_division_id: work.area_division_id,
    control_number: work.control_number,
    short_name: work.short_name,
    official_name: work.official_name,
    client_id: work.client_id,
    client_department: work.client_department,
    contractor_name: work.contractor_name,
    location: work.location,
    period_start: work.period_start,
    period_end: work.period_end,
    paint_kind: work.paint_kind,
    structure_form: work.structure_form,
    environment_category: work.environment_category,
    overhead_condition: work.overhead_condition,
  };
}

function loadTechnicianDrafts(workId: number | null, db: ExtendedDB): TechnicianDraft[] {
  if (workId === null) {
    return [];
  }
  const rows = db.workTechnicians.filter((t) => t.work_id === workId && t.deleted_at == null);
  rows.sort((a, b) => a.sort_order - b.sort_order);
  return rows.map((t) => ({
    id: t.id,
    origId: t.id,
    name: t.name,
    affiliation: t.affiliation,
    role: t.role,
    sort_order: t.sort_order,
    deleted: false,
  }));
}

function InfoEditTab({
  work,
  db,
  pageContext,
  onSaved,
}: {
  work: WorkBase | null;
  db: ExtendedDB;
  pageContext: PageContext;
  onSaved: (savedId: number) => void;
}) {
  const idb = pageContext.api.indexedDB;
  const ownerId = pageContext.api.currentUser.id;
  const isOnline = useIsOnline(pageContext.api);

  const [draft, setDraft] = useState<WorkDraft>(() => makeWorkDraft(work));
  const [technicians, setTechnicians] = useState<TechnicianDraft[]>(() => loadTechnicianDrafts(work?.id ?? null, db));
  const [savingState, setSavingState] = useState<"idle" | "saving" | "ok" | "err">("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // 保存済み状態のスナップショット(work の同一性で固定)。保存後は親が新しい work を渡すため再計算される。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const baseline = useMemo(() => JSON.stringify({ draft: makeWorkDraft(work), technicians: loadTechnicianDrafts(work?.id ?? null, db) }), [work]);
  const dirty = JSON.stringify({ draft, technicians: technicians.filter((t) => !(t.origId === null && t.deleted)) }) !== baseline;

  const setField = <K extends keyof WorkDraft>(key: K, value: WorkDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const addTechnician = () => {
    setTechnicians((prev) => {
      const maxSort = prev.reduce((m, t) => (t.sort_order > m ? t.sort_order : m), 0);
      const next: TechnicianDraft = {
        id: null,
        origId: null,
        name: "",
        affiliation: "",
        role: "専任技術者",
        sort_order: maxSort + 1,
        deleted: false,
      };
      return [...prev, next];
    });
  };

  const updateTechnician = (idx: number, patch: Partial<TechnicianDraft>) => {
    setTechnicians((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const removeTechnician = (idx: number) => {
    setTechnicians((prev) => {
      const target = prev[idx];
      if (target === undefined) {
        return prev;
      }
      if (target.origId === null) {
        // 未保存行はその場で消す
        return prev.filter((_, i) => i !== idx);
      }
      // 既存行は deleted フラグで保持(保存時に setDelete)
      return prev.map((t, i) => (i === idx ? { ...t, deleted: true } : t));
    });
  };

  const validate = (): string | null => {
    if (draft.control_number.trim() === "") {
      return "整理番号は必須です。";
    }
    if (draft.short_name.trim() === "") {
      return "工事の略称は必須です。";
    }
    if (draft.official_name.trim() === "") {
      return "工事の正式名称は必須です。";
    }
    if (draft.area_division_id === null) {
      return "工事型区分は必須です。";
    }
    if (draft.client_id === null) {
      return "発注者は必須です。";
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err !== null) {
      setErrorMessage(err);
      setSavingState("err");
      return;
    }
    setSavingState("saving");
    try {
      const savedWorkId = await saveWork();
      await saveTechnicians(savedWorkId);
      // 保存後ローカル状態を最新化
      if (work === null) {
        // 新規: techs を origId で同期
        setTechnicians((prev) => prev.filter((t) => !t.deleted).map((t) => ({ ...t, origId: t.id })));
      } else {
        // 既存: 削除済みを除外、id を確定
        setTechnicians((prev) => prev.filter((t) => !t.deleted).map((t) => ({ ...t, origId: t.id })));
      }
      setSavingState("ok");
      onSaved(savedWorkId);
    } catch (e) {
      setErrorMessage(`保存に失敗しました: ${errorMessageOf(e)}`);
      setSavingState("err");
    }
  };

  const saveWork = async (): Promise<number> => {
    const now = Time.Now().GetTime();
    const existingWorks = db.works ?? [];
    if (work === null) {
      const newId = await nextIdFor(idb, ownerId, "works");
      const newRow: WorkBase = {
        id: newId,
        createdAt: now,
        updatedAt: now,
        area_division_id: required(draft.area_division_id, "工事型区分"),
        control_number: draft.control_number,
        short_name: draft.short_name,
        official_name: draft.official_name,
        client_id: required(draft.client_id, "発注者"),
        client_department: draft.client_department,
        contractor_name: draft.contractor_name,
        location: draft.location,
        period_start: draft.period_start,
        period_end: draft.period_end,
        paint_kind: draft.paint_kind,
        structure_form: draft.structure_form,
        environment_category: draft.environment_category,
        overhead_condition: draft.overhead_condition,
        deleted_at: null,
      };
      await replaceCollection(idb, ownerId, "works", [...existingWorks, newRow], pageContext.api);
      return newId;
    }
    const updatedRow: WorkBase = {
      ...work,
      area_division_id: required(draft.area_division_id, "工事型区分"),
      control_number: draft.control_number,
      short_name: draft.short_name,
      official_name: draft.official_name,
      client_id: required(draft.client_id, "発注者"),
      client_department: draft.client_department,
      contractor_name: draft.contractor_name,
      location: draft.location,
      period_start: draft.period_start,
      period_end: draft.period_end,
      paint_kind: draft.paint_kind,
      structure_form: draft.structure_form,
      environment_category: draft.environment_category,
      overhead_condition: draft.overhead_condition,
      updatedAt: now,
    };
    const nextWorks = existingWorks.map((w) => {
      if (w.id === work.id) {
        return updatedRow;
      }
      return w;
    });
    await replaceCollection(idb, ownerId, "works", nextWorks, pageContext.api);
    return work.id;
  };

  const saveTechnicians = async (workId: number): Promise<void> => {
    const now = Time.Now().GetTime();
    const existingAll = db.workTechnicians ?? [];
    const unrelated = existingAll.filter((t) => t.work_id !== workId);
    const existingById = new Map<number, WorkTechnicianBase>();
    for (const ex of existingAll) {
      existingById.set(ex.id, ex);
    }

    // 新規行に id を採番
    let nextId = await nextIdFor(idb, ownerId, "workTechnicians");
    const nextRows: WorkTechnicianBase[] = [];
    for (const t of technicians) {
      if (t.origId === null && t.deleted) {
        // 追加してすぐ削除した行 → 何もしない
        continue;
      }
      if (t.origId === null) {
        // 新規行
        nextRows.push({
          id: nextId,
          createdAt: now,
          updatedAt: now,
          work_id: workId,
          name: t.name,
          affiliation: t.affiliation,
          role: t.role,
          sort_order: t.sort_order,
          deleted_at: null,
        });
        t.id = nextId;
        nextId += 1;
        continue;
      }
      // 既存行
      const prev = existingById.get(t.origId);
      if (prev === undefined) {
        // 既に存在しない: スキップ
        continue;
      }
      if (t.deleted) {
        nextRows.push({ ...prev, deleted_at: now, updatedAt: now });
        continue;
      }
      const changed = (
        prev.name !== t.name ||
        prev.affiliation !== t.affiliation ||
        prev.role !== t.role ||
        prev.sort_order !== t.sort_order
      );
      if (changed) {
        nextRows.push({
          ...prev,
          name: t.name,
          affiliation: t.affiliation,
          role: t.role,
          sort_order: t.sort_order,
          updatedAt: now,
        });
      } else {
        nextRows.push(prev);
      }
    }

    const allNext = [...unrelated, ...nextRows];
    await replaceCollection(idb, ownerId, "workTechnicians", allNext, pageContext.api);
  };

  const closeSnack = () => {
    if (savingState === "ok" || savingState === "err") {
      setSavingState("idle");
    }
  };

  const activeAreaDivisions = (db.areaDivisions ?? []).filter((d) => d.deleted_at == null);
  const activeClients = (db.clients ?? []).filter((c) => c.deleted_at == null);

  const visibleTechs = technicians.map((t, idx) => ({ t, idx })).filter((x) => !x.t.deleted);

  return (
    <Stack gap={2}>
      <Alert severity="info">{renderWord(WORD.workMaster.infoTabInfo)}</Alert>

      <Stack direction="row" gap={2}>
        <TextField
          select
          size="small"
          label="工事型区分"
          required
          value={draft.area_division_id ?? ""}
          onChange={(e) => setField("area_division_id", e.target.value === "" ? null : Number(e.target.value))}
          sx={{ flex: 1 }}
          helperText={WORD.workMaster.hAreaDivision}
        >
          {activeAreaDivisions.map((d) => (
            <MenuItem key={d.id} value={d.id}>#{d.id} {d.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="整理番号"
          required
          value={draft.control_number}
          onChange={(e) => setField("control_number", e.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="工事の略称"
          required
          value={draft.short_name}
          onChange={(e) => setField("short_name", e.target.value)}
          sx={{ flex: 1 }}
        />
      </Stack>

      <TextField
        size="small"
        label="工事の正式名称"
        required
        value={draft.official_name}
        onChange={(e) => setField("official_name", e.target.value)}
        fullWidth
      />

      <Stack direction="row" gap={2}>
        <TextField
          select
          size="small"
          label="発注者"
          required
          value={draft.client_id ?? ""}
          onChange={(e) => setField("client_id", e.target.value === "" ? null : Number(e.target.value))}
          sx={{ flex: 1 }}
        >
          {activeClients.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="発注者部署"
          value={draft.client_department}
          onChange={(e) => setField("client_department", e.target.value)}
          sx={{ flex: 1 }}
        />
      </Stack>

      <TextField
        size="small"
        label="受注者名称"
        value={draft.contractor_name}
        onChange={(e) => setField("contractor_name", e.target.value)}
        fullWidth
      />
      <TextField
        size="small"
        label="工事場所"
        value={draft.location}
        onChange={(e) => setField("location", e.target.value)}
        fullWidth
        multiline
        minRows={2}
      />

      <Stack direction="row" gap={2}>
        <TextField
          type="date"
          size="small"
          label="工期開始"
          InputLabelProps={{ shrink: true }}
          value={draft.period_start}
          onChange={(e) => setField("period_start", e.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          type="date"
          size="small"
          label="工期終了"
          InputLabelProps={{ shrink: true }}
          value={draft.period_end}
          onChange={(e) => setField("period_end", e.target.value)}
          sx={{ flex: 1 }}
        />
      </Stack>

      <Stack direction="row" gap={2}>
        <TextField
          size="small"
          label="塗装種類"
          value={draft.paint_kind}
          onChange={(e) => setField("paint_kind", e.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="構造形式"
          value={draft.structure_form}
          onChange={(e) => setField("structure_form", e.target.value)}
          sx={{ flex: 1 }}
        />
      </Stack>

      <Stack direction="row" gap={2}>
        <TextField
          select
          size="small"
          label="環境区分"
          value={draft.environment_category}
          onChange={(e) => setField("environment_category", e.target.value)}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">（未選択）</MenuItem>
          {["海岸", "工場地帯", "市街地", "田園", "山間"].map((o) => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="高架下条件"
          value={draft.overhead_condition}
          onChange={(e) => setField("overhead_condition", e.target.value)}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">（未選択）</MenuItem>
          {overheadConditionOptions(draft.overhead_condition).map((o) => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle1" fontWeight="bold">監理・専任技術者</Typography>
      <Stack gap={1}>
        {visibleTechs.length === 0 && (
          <Typography variant="caption" color="text.secondary">技術者が登録されていません。</Typography>
        )}
        {visibleTechs.map(({ t, idx }) => (
          <Stack key={`tech-${idx}`} direction="row" gap={1} alignItems="center">
            <TextField
              size="small"
              label="氏名"
              value={t.name}
              onChange={(e) => updateTechnician(idx, { name: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="所属"
              value={t.affiliation}
              onChange={(e) => updateTechnician(idx, { affiliation: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="役職"
              value={t.role}
              onChange={(e) => updateTechnician(idx, { role: e.target.value })}
              sx={{ width: 200 }}
            />
            <IconButton size="small" disabled={!isOnline} onClick={() => removeTechnician(idx)} title="技術者を削除">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Box>
          <Button startIcon={<AddIcon />} size="small" disabled={!isOnline} onClick={addTechnician}>技術者を追加</Button>
        </Box>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" gap={1} mt={2} alignItems="center">
        {dirty && savingState !== "saving" && <Typography variant="caption" color="warning.main">未保存の変更があります</Typography>}
        <Button variant="contained" onClick={save} disabled={savingState === "saving" || !isOnline || !dirty}>
          {savingState === "saving" ? "保存中…" : "保存"}
        </Button>
      </Stack>

      <Snackbar
        open={savingState === "ok"}
        autoHideDuration={2500}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        message="保存しました"
      />
      <Snackbar
        open={savingState === "err"}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        message={errorMessage || "保存に失敗"}
      />
    </Stack>
  );
}

// ===== labels タブ(参照のみ) =====
function LabelSettingTab({ work, db }: { work: WorkBase; db: ExtendedDB }) {
  const ad = db.areaDivisions.find((d) => d.id === work.area_division_id);
  const labels = db.areaDivisionLocationLabels
    .filter((l) => l.area_division_id === work.area_division_id && l.deleted_at == null && l.label_name !== "")
    .sort((a, b) => a.slot - b.slot);

  return (
    <Stack gap={2}>
      <Alert severity="info">{renderWord(WORD.workMaster.labelsTabInfo(ad?.id ?? "-", ad?.name ?? "(未設定)"))}</Alert>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", width: 100 }}>スロット</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>ラベル名</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 120 }}>必須</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((slot) => {
              const setting = db.areaDivisionLocationLabels.find(
                (l) => l.area_division_id === work.area_division_id && l.slot === slot && l.deleted_at == null
              );
              return (
                <TableRow key={slot}>
                  <TableCell>場所区分{slot}</TableCell>
                  <TableCell>{setting?.label_name || "-"}</TableCell>
                  <TableCell>
                    {setting ? (
                      <Chip
                        size="small"
                        color={setting.is_required ? "error" : "default"}
                        label={setting.is_required ? "必須" : "任意"}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="subtitle2" mt={2}>現在のラベル + 選択肢プレビュー</Typography>
      <Stack direction="row" gap={1} flexWrap="wrap">
        {labels.map((l) => {
          const opts = db.locationDivisions.filter(
            (d) => d.slot === l.slot && d.area_division_id === work.area_division_id && d.deleted_at == null
          );
          return (
            <Paper key={l.slot} variant="outlined" sx={{ p: 1.5, minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary">区分{l.slot}</Typography>
              <Typography variant="subtitle2" fontWeight="bold">{l.label_name}</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                {opts.slice(0, 5).map((o) => (
                  <Chip key={o.id} size="small" label={o.division_name} />
                ))}
                {opts.length > 5 && <Chip size="small" label={`+${opts.length - 5}`} />}
              </Stack>
            </Paper>
          );
        })}
        {labels.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            ラベル未設定です。「マスタ編集」→「工事型区分」マスタから設定してください。
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

// ===== access タブ(参照のみ) =====
function AccessScopeTab({ work, db }: { work: WorkBase; db: ExtendedDB }) {
  const techs = db.workTechnicians
    .filter((t) => t.work_id === work.id && t.deleted_at == null)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <Stack gap={2}>
      <Alert severity="info">{renderWord(WORD.workMaster.accessTabInfo)}</Alert>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>氏名</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>所属</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>役職</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {techs.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{t.name || "-"}</TableCell>
                <TableCell>{t.affiliation || "-"}</TableCell>
                <TableCell>
                  <Chip size="small" label={t.role || "-"} />
                </TableCell>
              </TableRow>
            ))}
            {techs.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  技術者が登録されていません。「工事情報」タブから追加してください。
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}
